import {
  EVENTO_INTERNO,
  NS_INTERNO,
  type EventoDigitandoInterno,
  type EventoLeituraInterna,
  type EventoMensagemInterna,
  type EventoPresencaInterna,
} from '@mymaxx/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';
import { create } from 'zustand';
import { Telefone } from '../paginas/ChamadaDeVoz';
import { tentarRefresh } from './api';
import { lerAccessToken, useSessao } from './sessao';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

/** Folga antes do vencimento: token que morre no meio do handshake não serve. */
const MARGEM_VALIDADE_MS = 60_000;

/** Quando este access token vence, lido do próprio JWT. `null` = não consegui ler. */
function venceEm(token: string): number | null {
  const corpo = token.split('.')[1];
  if (!corpo) return null;
  try {
    const dados = JSON.parse(atob(corpo.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return typeof dados.exp === 'number' ? dados.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * O token deste handshake — renovado quando está para vencer.
 *
 * O access token dura 15 minutos e o socket reconecta sozinho: celular que dormiu no
 * bolso, 4G que caiu entre dois prédios, API que subiu num deploy. Mandar o token da
 * abertura faria a reconexão ser recusada para sempre a partir dali — a tela continuaria
 * aberta e simplesmente pararia de receber. Foi esse defeito no painel, em 28/09.
 */
async function tokenParaHandshake(): Promise<string | null> {
  const atual = lerAccessToken();
  const vencimento = atual ? venceEm(atual) : null;
  if (atual && (vencimento === null || vencimento - Date.now() > MARGEM_VALIDADE_MS)) return atual;
  await tentarRefresh();
  return lerAccessToken();
}

const ContextoInterno = createContext<Socket | null>(null);

/**
 * A conexão da conversa interna no app do técnico, viva enquanto ele estiver logado.
 *
 * Fica no alto do aplicativo, e não dentro da tela de Conversas, porque **estar conectado
 * aqui é o que diz à equipe que ele está com o MyMAXX aberto**. Vivendo só na tela de
 * conversas, o técnico apareceria offline justamente enquanto estivesse num atendimento —
 * que é onde ele passa o tempo.
 *
 * Só para técnico: o cliente comum não participa de canal nenhum, e abrir um socket para
 * ele seria conexão sem assunto.
 */
export function ProvedorInterno({ children }: { children: ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const clienteId = useSessao((e) => e.cliente?.id);
  const ehTecnico = useSessao((e) => !!e.cliente?.tecnico);
  const qc = useQueryClient();

  useEffect(() => {
    if (!clienteId || !ehTecnico) return;
    const s: Socket = io(`${BASE_URL}${NS_INTERNO}`, {
      // Os dois transportes: atrás de proxy a subida direta para WebSocket às vezes falha,
      // e sem o polling de reserva o canal simplesmente não conecta.
      transports: ['websocket', 'polling'],
      auth: (entregar) => {
        void tokenParaHandshake().then((token) => entregar({ token: token ?? '' }));
      },
    });
    setSocket(s);

    // A lista vale em qualquer tela: é dela que sai o contador na conta do técnico.
    const recarregar = () => void qc.invalidateQueries({ queryKey: ['interno-canais'] });
    s.on(EVENTO_INTERNO.MENSAGEM, recarregar);
    s.on(EVENTO_INTERNO.CANAL, recarregar);

    return () => {
      s.off(EVENTO_INTERNO.MENSAGEM, recarregar);
      s.off(EVENTO_INTERNO.CANAL, recarregar);
      s.disconnect();
      setSocket(null);
    };
  }, [clienteId, ehTecnico, qc]);

  // O "digitando" de qualquer conversa, para a linha da lista mostrar como no WhatsApp.
  // Fica aqui porque a tela de Conversas só conhece a conversa aberta.
  useEffect(() => {
    if (!socket) return;
    const digitando = (e: EventoDigitandoInterno) =>
      useDigitando.getState().registrar(e.canalId, e.nome);
    socket.on(EVENTO_INTERNO.DIGITANDO, digitando);
    const varredura = setInterval(() => useDigitando.getState().varrer(), 1_000);
    return () => {
      socket.off(EVENTO_INTERNO.DIGITANDO, digitando);
      clearInterval(varredura);
    };
  }, [socket]);

  return (
    <ContextoInterno.Provider value={socket}>
      {children}
      {/* O telefone fica aqui: o técnico pode estar no histórico, na conta ou num
          atendimento quando alguém da equipe ligar. */}
      <Telefone />
    </ContextoInterno.Provider>
  );
}

/** O socket da conversa interna, para quem precisa falar com o servidor por ele. */
export function useSocketInterno(): Socket | null {
  return useContext(ContextoInterno);
}

/** Ouvir o que chega, de dentro de uma tela. Os ouvintes trocam sem reinscrever o socket. */
export function useEventosInternos(ouvintes: {
  aoReceberMensagem?: (e: EventoMensagemInterna) => void;
  aoMudarMensagem?: (e: EventoMensagemInterna) => void;
  aoMudarCanal?: (canalId: string) => void;
  aoLer?: (e: EventoLeituraInterna) => void;
  aoDigitar?: (e: EventoDigitandoInterno) => void;
  aoMudarPresenca?: (e: EventoPresencaInterna) => void;
}): void {
  const socket = useContext(ContextoInterno);
  const ref = useRef(ouvintes);
  ref.current = ouvintes;

  useEffect(() => {
    if (!socket) return;
    const mensagem = (e: EventoMensagemInterna) => ref.current.aoReceberMensagem?.(e);
    const mudou = (e: EventoMensagemInterna) => ref.current.aoMudarMensagem?.(e);
    const canal = (e: { canalId: string }) => ref.current.aoMudarCanal?.(e.canalId);
    const lido = (e: EventoLeituraInterna) => ref.current.aoLer?.(e);
    const digitando = (e: EventoDigitandoInterno) => ref.current.aoDigitar?.(e);
    const presenca = (e: EventoPresencaInterna) => ref.current.aoMudarPresenca?.(e);

    socket.on(EVENTO_INTERNO.MENSAGEM, mensagem);
    socket.on(EVENTO_INTERNO.MENSAGEM_MUDOU, mudou);
    socket.on(EVENTO_INTERNO.CANAL, canal);
    socket.on(EVENTO_INTERNO.LIDO, lido);
    socket.on(EVENTO_INTERNO.DIGITANDO, digitando);
    socket.on(EVENTO_INTERNO.PRESENCA, presenca);
    return () => {
      socket.off(EVENTO_INTERNO.MENSAGEM, mensagem);
      socket.off(EVENTO_INTERNO.MENSAGEM_MUDOU, mudou);
      socket.off(EVENTO_INTERNO.CANAL, canal);
      socket.off(EVENTO_INTERNO.LIDO, lido);
      socket.off(EVENTO_INTERNO.DIGITANDO, digitando);
      socket.off(EVENTO_INTERNO.PRESENCA, presenca);
    };
  }, [socket]);
}

/** Avisa que estou escrevendo. A tela segura o volume; aqui é só o envio. */
export function useAvisarQueDigito(): (canalId: string) => void {
  const socket = useContext(ContextoInterno);
  return (canalId: string) => socket?.emit(EVENTO_INTERNO.DIGITANDO, { canalId });
}

/** Quanto tempo um aviso de "digitando" vale. A tela manda um a cada três segundos. */
const VALIDADE_DIGITANDO_MS = 4_000;

/** Devolvido quando ninguém digita: um array fixo não dispara redesenho à toa. */
const NINGUEM: string[] = [];

/**
 * Quem está escrevendo, em qualquer conversa.
 *
 * Fica na moldura, e não na tela de Conversas, porque o WhatsApp mostra o "digitando" **na
 * linha da lista** — e para isso é preciso saber de todas as conversas, não só da que está
 * aberta. Guarda nome → até quando o aviso vale: quem para de escrever não manda nada
 * dizendo que parou, e a última tecla é tudo o que se sabe.
 */
export const useDigitando = create<{
  porCanal: Record<string, Record<string, number>>;
  registrar: (canalId: string, nome: string) => void;
  varrer: () => void;
}>((set) => ({
  porCanal: {},
  registrar: (canalId, nome) =>
    set((e) => ({
      porCanal: {
        ...e.porCanal,
        [canalId]: { ...e.porCanal[canalId], [nome]: Date.now() + VALIDADE_DIGITANDO_MS },
      },
    })),
  varrer: () =>
    set((e) => {
      const agora = Date.now();
      const novo: Record<string, Record<string, number>> = {};
      let mudou = false;
      for (const [canalId, pessoas] of Object.entries(e.porCanal)) {
        const vivos = Object.fromEntries(Object.entries(pessoas).filter(([, ate]) => ate > agora));
        if (Object.keys(vivos).length > 0) novo[canalId] = vivos;
        if (Object.keys(vivos).length !== Object.keys(pessoas).length) mudou = true;
      }
      // Devolver o mesmo objeto quando nada venceu evita redesenhar a lista a cada segundo.
      return mudou ? { porCanal: novo } : e;
    }),
}));

/**
 * Os nomes de quem está escrevendo neste canal agora.
 *
 * Assina só o pedaço deste canal, e não o mapa inteiro: com sessenta conversas na lista,
 * assinar o mapa faria cada tecla de qualquer pessoa redesenhar as sessenta linhas.
 */
export function useQuemDigita(canalId: string | null): string[] {
  const pessoas = useDigitando((e) => (canalId ? e.porCanal[canalId] : undefined));
  if (!pessoas) return NINGUEM;
  const agora = Date.now();
  return Object.entries(pessoas)
    .filter(([, ate]) => ate > agora)
    .map(([nome]) => nome);
}
